import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_la-fleur-studio/artifacts/nadzwxji_4CF2582D-F4EB-44B1-8A44-D7BD98DFD14F.PNG";

const SHOP_LINKS = [
  { to: "/shop?category=bouquets", label: "Bouquets" },
  { to: "/shop?category=balloons", label: "Balloons" },
  { to: "/shop?category=sweets",   label: "Sweets" },
  { to: "/shop?category=merch",    label: "Merch" },
  { to: "/shop?category=gifts",    label: "Gifts" },
];
const SERVICE_LINKS = [
  { to: "/services/flower-bar",   label: "Flower Bar" },
  { to: "/services/bridal",    label: "Bridal & Weddings" },
  { to: "/services/memorial",  label: "Memorial & Sympathy" },
  { to: "/services/supplies",  label: "Florist Supplies" },
];

const NavDropdown = ({ label, links, testId }) => {
  const [open, setOpen] = React.useState(false);
  let closeTimer;
  const onEnter = () => { clearTimeout(closeTimer); setOpen(true); };
  const onLeave = () => { closeTimer = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button data-testid={testId} className="lf-link inline-flex items-center gap-1.5">
        {label}
        <ChevronDown size={13} strokeWidth={2} />
      </button>
      {open && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[230px] py-3 px-1"
          style={{
            background: "white",
            border: "1px solid var(--lf-border)",
            borderRadius: 18,
            boxShadow: "0 18px 40px -16px rgba(58,46,42,0.22)",
          }}
        >
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`dropdown-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="block px-5 py-2.5 text-sm font-medium hover:bg-[var(--lf-pink-pale)] rounded-xl transition-colors"
              style={{ color: "var(--lf-ink)" }}
              onClick={() => setOpen(false)}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navigation = () => {
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <header
      data-testid="lf-navigation"
      className="sticky top-0 z-40 backdrop-blur-xl border-b"
      style={{ background: "rgba(247, 239, 227, 0.88)", borderColor: "var(--lf-border)" }}
    >
      <div className="lf-container flex items-center justify-between" style={{ height: 90 }}>
        {/* Left links */}
        <nav className="hidden md:flex items-center gap-8 flex-1">
          <NavLink to="/" data-testid="nav-home" className={({ isActive }) => `lf-link ${isActive ? "text-[var(--lf-gold-deep)]" : ""}`}>Home</NavLink>
          <NavDropdown label="Shop" links={SHOP_LINKS} testId="nav-shop" />
          <NavDropdown label="Services" links={SERVICE_LINKS} testId="nav-services" />
        </nav>

        {/* Logo — removed per user request; hero wordmark is the brand identity */}
        <div className="flex-1 md:flex-none md:w-16" aria-hidden="true"></div>

        {/* Right links */}
        <nav className="hidden md:flex items-center gap-8 flex-1 justify-end">
          <NavLink to="/gallery" data-testid="nav-gallery" className={({ isActive }) => `lf-link ${isActive ? "text-[var(--lf-gold-deep)]" : ""}`}>Gallery</NavLink>
          <NavLink to="/reviews" data-testid="nav-reviews" className={({ isActive }) => `lf-link ${isActive ? "text-[var(--lf-gold-deep)]" : ""}`}>Reviews</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={({ isActive }) => `lf-link ${isActive ? "text-[var(--lf-gold-deep)]" : ""}`}>About</NavLink>
          <button data-testid="nav-cart-button" onClick={() => setIsOpen(true)} className="relative flex items-center gap-2 lf-link">
            <ShoppingBag size={18} strokeWidth={1.5} />
            <span>Cart</span>
            {count > 0 && (
              <span data-testid="cart-count-badge"
                className="absolute -top-2 -right-3 text-[10px] flex items-center justify-center"
                style={{ background: "var(--lf-gold)", color: "white", width: 18, height: 18, borderRadius: 9999 }}>
                {count}
              </span>
            )}
          </button>
        </nav>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-4">
          <button data-testid="nav-cart-button-mobile" onClick={() => setIsOpen(true)} className="relative" aria-label="Open cart">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] flex items-center justify-center"
                style={{ background: "var(--lf-gold)", color: "white", width: 16, height: 16, borderRadius: 9999 }}>
                {count}
              </span>
            )}
          </button>
          <button data-testid="nav-menu-toggle" onClick={() => setMobileOpen((s) => !s)} aria-label="Toggle menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden border-t" style={{ borderColor: "var(--lf-border)", background: "var(--lf-cream)" }}>
          <div className="lf-container py-6 flex flex-col gap-4">
            <Link to="/" onClick={() => setMobileOpen(false)} className="lf-link">Home</Link>
            <div className="lf-eyebrow mt-2">Shop</div>
            {SHOP_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="lf-link" data-testid={`mobile-shop-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
            <div className="lf-eyebrow mt-2">Services</div>
            {SERVICE_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="lf-link" data-testid={`mobile-svc-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <div className="lf-eyebrow mt-2">More</div>
            <Link to="/gallery" onClick={() => setMobileOpen(false)} className="lf-link">Gallery</Link>
            <Link to="/reviews" onClick={() => setMobileOpen(false)} className="lf-link">Reviews</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="lf-link">About</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
